import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { organisationsApi } from '../../lib/api';
import { logActivity } from '../../lib/activityLog';
import { SupportOrgList } from '../mylife/SupportHint';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';

// Mirrors services/org_summary.CONTENT_NOTES.
const NOTE_LABELS = {
  neglect: 'neglect',
  physical: 'physical harm',
  emotional: 'emotional harm',
  sexual: 'sexual harm',
  exploitation: 'exploitation',
  domestic_abuse: 'domestic abuse',
  self_harm: 'self-harm',
  missing: 'going missing',
  restraint_or_isolation: 'being restrained or shut away',
  not_believed: 'not being believed',
  separation: 'being separated from family',
  placement_moves: 'lots of moves',
  bereavement: 'loss and bereavement',
  police: 'police involvement',
  other: 'other difficult things',
};

const listWords = (items) => {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const monthYear = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : null;
};

// Everything from the organisation has finished analysis (nothing queued or
// in progress) and at least one document has something to read — mirrors
// services/org_summary.org_ready, which gates the automatic write.
export const analysisSettled = (documents = []) => (
  documents.length > 0
  && !documents.some((d) => d.analysis_status === 'queued' || d.analysis_status === 'analysing')
  && documents.some((d) => d.analysis_status === 'complete' || d.analysis_status === 'partial')
);

/**
 * "Your time with …": the summary of what an organisation's records say
 * about the person's time with them. Written automatically once their
 * documents are analysed (services/org_summary.py) and never shown until
 * opened. A hard one opens behind a gentle screen first — what it
 * mentions, read now / not now, and support — the same care-wash pattern
 * as a sensitive record (SupportHint). Both the screen and the summary open
 * in one dialog over the page, never inline. The screen can't be skipped by the
 * AI's own rating: any concern in the records raises it server-side.
 *
 * There's no button to write it: it's rewritten whenever the organisation's
 * documents finish analysis. If this page finds none (or a failed one) once
 * analysis has settled — e.g. written before AI was set up — it asks for one
 * itself, once per visit, since only the browser holds the key.
 */
export const OrgSummary = ({ orgId, orgName, onOpenRecord, ready = false }) => {
  const [status, setStatus] = useState(null);
  const [view, setView] = useState('closed'); // closed | opening | gate | reading | locked | error
  const [data, setData] = useState(null);
  const alive = useRef(true);
  const askedFor = useRef(null);
  useEffect(() => () => { alive.current = false; }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await organisationsApi.getSummaryStatus(orgId);
      if (alive.current) setStatus(r.data || { status: 'none' });
    } catch {
      if (alive.current) setStatus({ status: 'none' });
    }
  }, [orgId]);

  useEffect(() => { setView('closed'); setData(null); setStatus(null); loadStatus(); }, [loadStatus]);

  // While it's being written, check back now and then.
  useEffect(() => {
    if (status?.status !== 'writing') return undefined;
    const t = setInterval(loadStatus, 10000);
    return () => clearInterval(t);
  }, [status?.status, loadStatus]);

  const open = async () => {
    setView('opening');
    try {
      const r = await organisationsApi.openSummary(orgId);
      if (!alive.current) return;
      if (r.data?.locked) { setView('locked'); return; }
      setData(r.data);
      if (r.data?.summary?.tone === 'hard') {
        setView('gate');
        logActivity('org_summary_gate_shown', { tone: 'hard' });
      } else {
        setView('reading');
        logActivity('org_summary_opened', { tone: r.data?.summary?.tone || 'unknown' });
      }
    } catch {
      if (alive.current) setView('error');
    }
  };

  // No summary yet (or the last try failed) and analysis has settled: ask
  // for one quietly, once per organisation per visit.
  useEffect(() => {
    if (!ready || !status || askedFor.current === orgId) return;
    if (status.status !== 'none' && !(status.status === 'failed' && !status.has_summary)) return;
    askedFor.current = orgId;
    organisationsApi.refreshSummary(orgId)
      .then((r) => {
        if (alive.current && !r.data?.locked) setStatus((prev) => ({ ...(prev || {}), status: 'writing' }));
      })
      .catch(() => {});
  }, [ready, status, orgId]);

  if (!status || status.status === 'empty') return null;
  const s = data?.summary;
  const notes = (s?.content_notes || []).map((n) => NOTE_LABELS[n]).filter(Boolean);
  const support = data?.support_orgs || [];
  const onSupportClick = (org, type) => logActivity('support_org_clicked', { organisation: org.name, category: type });

  const close = (fromGate = view === 'gate') => {
    if (fromGate) logActivity('org_summary_gate_later', {});
    setView('closed');
    setData(null);
  };
  const dialogOpen = (view === 'gate' || view === 'reading') && !!s;

  return (
    <section className="space-y-3" aria-labelledby="org-summary-heading" data-testid="org-summary">
      <h2 id="org-summary-heading" className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">
        Your time with {orgName}
      </h2>

      <div className="rounded-lg bg-ground p-5 space-y-3">
        {status.status === 'writing' ? (
          <p className="text-[16px] text-ink-70 flex items-center gap-2" data-testid="org-summary-writing">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            We're writing a summary of what their records say about your time with them. It won't open until you choose to.
          </p>
        ) : status.status === 'ready' || status.has_summary ? (
          <>
            <p className="text-[16px] text-ink leading-relaxed">
              A summary of what {orgName}'s records say about your time with them — what it seems to have been like, and what stands out. Open it when you're ready.
            </p>
            <Button size="sm" onClick={open} disabled={view === 'opening'} data-testid="org-summary-open">
              {view === 'opening' ? 'Opening…' : 'Read the summary'}
            </Button>
          </>
        ) : (
          <p className="text-[16px] text-ink leading-relaxed" data-testid="org-summary-pending">
            {status.status === 'failed'
              ? 'We couldn’t write the summary this time. We’ll try again next time you open this page, or when new documents arrive.'
              : `Once everything from ${orgName} has been analysed, we’ll write a summary of what their records say about your time with them. It won’t open until you choose to.`}
          </p>
        )}
        {view === 'locked' && <p className="text-sm text-ink-55">Unlock your account first — the summary needs your key to open.</p>}
        {view === 'error' && <p className="text-sm text-ink-55">That didn't work — try again in a little while.</p>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent
          className="p-0 gap-0 flex flex-col overflow-hidden sm:max-w-[720px] w-[96vw] max-h-[min(760px,92dvh)] max-sm:w-full max-sm:max-h-[96dvh] max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-t-xl max-sm:rounded-b-none"
          data-testid="org-summary-dialog"
        >
          {view === 'gate' && s && (
            <>
              <div className="flex-1 overflow-y-auto px-5 sm:px-8 pt-6 pb-5 space-y-4" data-testid="org-summary-gate">
                <DialogHeader className="space-y-2 pr-8">
                  <p className="pd-eyebrow">Your time with {orgName}</p>
                  <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">
                    Before you read this
                  </DialogTitle>
                </DialogHeader>
                <div className="rounded-lg bg-care-wash p-5">
                  <DialogDescription className="text-[17px] leading-relaxed text-ink">
                    {notes.length
                      ? `This summary talks about ${listWords(notes)}. It may be hard to read.`
                      : 'Some of what this summary describes may be hard to read.'}
                    {' '}You can read it now, come back to it another time, or read it with someone you trust. Nothing is lost if you wait.
                  </DialogDescription>
                </div>
                {support.length > 0 && <SupportOrgList orgs={support} onOrgClick={onSupportClick} testId="org-summary-support" />}
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap px-5 sm:px-8 py-4">
                <Button size="sm" variant="ghost" onClick={() => close(true)} data-testid="org-summary-later">
                  Not now
                </Button>
                <Button size="sm" onClick={() => { setView('reading'); logActivity('org_summary_opened', { tone: 'hard' }); }} data-testid="org-summary-read-now">
                  Read it now
                </Button>
              </div>
            </>
          )}

          {view === 'reading' && s && (
            <>
              <article className="flex-1 overflow-y-auto px-5 sm:px-8 pt-6 pb-5 space-y-5" data-testid="org-summary-text">
                <DialogHeader className="space-y-1 pr-8">
                  <p className="pd-eyebrow">Your time with {orgName}{s.period ? ` · ${s.period}` : ''}</p>
                  <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink">
                    {s.headline || `Your time with ${orgName}`}
                  </DialogTitle>
                  <DialogDescription className="sr-only">A summary written from the records {orgName} sent.</DialogDescription>
                </DialogHeader>
                {s.tone === 'mixed' && (
                  <p className="rounded-md bg-care-wash px-4 py-3 text-[15px] text-ink" data-testid="org-summary-mixed-note">
                    Some of this may be sad to read. You can stop whenever you want.
                  </p>
                )}
                <div className="space-y-3 text-[17px] leading-relaxed text-ink max-w-prose">
                  {s.summary.slice(0, 4).map((p, i) => <p key={i}>{p}</p>)}
                </div>
                {s.stands_out?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-[15px] font-semibold text-ink">What stands out</h4>
                    <ul className="space-y-2">
                      {s.stands_out.slice(0, 3).map((p, i) => (
                        <li key={i} className="text-[16px] text-ink leading-relaxed">
                          {p.point}
                          {onOpenRecord && p.record_ids?.length > 0 && (
                            <>
                              {' '}
                              <button
                                type="button"
                                onClick={() => { close(false); onOpenRecord(p.record_ids[0]); }}
                                className="text-sm font-semibold text-brand hover:text-brand-ink"
                              >
                                Open the record
                              </button>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {s.not_covered && (
                  <div className="space-y-1">
                    <h4 className="text-[15px] font-semibold text-ink">What the records don't say much about</h4>
                    <p className="text-[16px] text-ink leading-relaxed">{s.not_covered}</p>
                  </div>
                )}
                <p className="text-sm text-ink-55 leading-relaxed">
                  Written by an AI from {data.records_count || 'the'} record{data.records_count === 1 ? '' : 's'} {orgName} sent
                  {monthYear(data.written_at) ? `, in ${monthYear(data.written_at)}` : ''}. It can be wrong or miss things, and records
                  only show what adults wrote down at the time — and only what was sent. You know your own life best.
                </p>
              </article>
              <div className="flex items-center justify-end gap-2 flex-wrap px-5 sm:px-8 py-4">
                <Button size="sm" variant="secondary" onClick={() => close(false)} data-testid="org-summary-close">Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default OrgSummary;
